import {KeycloakService} from 'keycloak-angular';
import {Component, Input, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {GenericListDialogComponent} from '../../dialogs/generic-list-dialog/generic-list-dialog.component';
import {IssueTicketService} from '../../../other/services/issue-ticket.service';
import {PagedRequestDTO} from '../../models/PagedRequestDTO';
import {PagedFilterDTO} from '../../models/PagedFilterDTO';
import {environment} from '../../../../../environments/environment';
import {CacheService} from '../../services/cache.service';
import {SharedService} from '../../services/shared-service';
import {CaseStatus} from '../../../clm/types';
import {UserAuthorisationService} from '../../services/user-authorisation.service';
import {EnvironmentService} from '../../services/environment-service';
import {
  ClaimRoutesQueryParam, DataCenterRoutesQueryParam,
  EnterpriseRoutesQueryParam,
  PharmacyRoutesQueryParam
} from '../../types/routes-query-param.enum';
import {TabLabel, TabDataCenterLabel} from '../../types';
import {
  MemberLookupDialogComponent
} from 'src/app/modules/other/components/dialogs/member-lookup-dialog/member-lookup-dialog.component';
import {EntityTable} from '../../types/entity-table.enum';
import {CaseService} from '../../../clm/services/case.service';
import {EnvironmentConfig} from '../../../../../environments/environment.config';
import {
  AdhocIdVerificationDialogComponent
} from 'src/app/modules/other/components/dialogs/adhoc-id-verification-dialog/adhoc-id-verification-dialog.component';
import {
  AdhocBankVerificationDialogComponent
} from '../../../other/components/dialogs/adhoc-bank-verification-dialog/adhoc-bank-verification-dialog.component';
import {DisplayStrings} from '../../constants/DisplayStrings';

enum SortFilterValue {
  TICKET_ASSIGNEE_FIRST_NAME = 'assigneeByUserId.firstName',
  TICKET_ASSIGNEE_LAST_NAME = 'assigneeByUserId.lastName',
  TICKET_STATUS = 'statusByStatusId.name',
  CASE_STATUS = 'caseStatus',
  CASE_MANAGER = 'caseManager'
}

const OPEN_STATUS = 'Open';

@Component({
  selector: 'app-top-menu',
  templateUrl: './top-menu.component.html',
  styleUrls: ['./top-menu.component.scss']
})
export class TopMenuComponent implements OnInit {
  readonly caseStatus = CaseStatus;
  readonly DisplayStrings = DisplayStrings;
  readonly TabLabel = TabLabel;
  readonly TabDataCenterLabel = TabDataCenterLabel;
  readonly TabClaimsQueryParams = ClaimRoutesQueryParam;
  readonly TabEnterpriseQueryParams = EnterpriseRoutesQueryParam;
  readonly TabPharmacyQueryParams = PharmacyRoutesQueryParam;
  readonly TabDataCenterQueryParams = DataCenterRoutesQueryParam;
  readonly Themes = EnvironmentConfig.themes;
  readonly prodEnvironmentLabel = 'Production';
  readonly greydomSiteURL = 'https://greydom.com/';

  @Input() theme: string;

  userFirstName: string;
  userLastName: string;
  numberOfOpenTickets: number;
  numberOfOpenCases: number;
  environmentName: string;
  loggedInUserName: string;
  isProviderPorterRole: boolean;
  isOnboardingAdminPortalRole: boolean;
  isBrokerPortalRole: boolean;
  hasNormalPortalAccess: boolean;
  hasProviderPortalAccess: boolean;
  hasBrokerPortalAccess: boolean;
  showSettingsIcon: boolean;
  showPoweredByOverride: boolean;
  hasSearchMemberPermissions = false;
  showCases: boolean;
  showPayments: boolean;
  private readonly inboxFeatureEnabled: boolean;

  // Inbox: prepared authorisation drafts. Same people who capture authorisations, plus admins.
  get showInbox(): boolean {
    return this.inboxFeatureEnabled &&
      (this.userAuthorisationService.hasRoleToSubmitAndReviewTransactions() || this.userAuthorisationService.hasRoleAdmin());
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private ticketService: IssueTicketService,
    private caseService: CaseService,
    private cacheService: CacheService,
    private sharedService: SharedService,
    private keycloakService: KeycloakService,
    private environmentService: EnvironmentService,
    private userAuthorisationService: UserAuthorisationService
  ) {
    this.userFirstName = '';
    this.userLastName = '';
    this.numberOfOpenTickets = 0;
    this.numberOfOpenCases = 0;
    this.environmentName = this.environmentService.getCurrentEnvironmentName();
    this.hasNormalPortalAccess = this.environmentService.isAllowedToAccessNormalPortal();
    this.hasProviderPortalAccess = this.environmentService.isAllowedToAccessProviderPortal();
    this.hasBrokerPortalAccess = this.environmentService.isAllowedToAccessBrokerPortal();
    this.hasSearchMemberPermissions = this.hasMemberVerificationAccess();
    this.showCases = this.environmentService.isCasesFeatureEnabled();
    this.showPayments = this.environmentService.isPaymentsFeatureEnabled();
    this.inboxFeatureEnabled = this.environmentService.isInboxFeatureEnabled();
  }

  ngOnInit(): void {
    this.keycloakService.loadUserProfile().then((profile) => {
      this.userFirstName = `${profile.firstName}`;
      this.userLastName = `${profile.lastName}`;
      this.loggedInUserName = `${profile.firstName} ${profile.lastName}`;

      this.showSettingsIcon = this.userAuthorisationService.hasRoleAdmin();

      if (this.userAuthorisationService.hasRoleProvider()) {
        this.isProviderPorterRole = true;
      }

      if (this.userAuthorisationService.hasOnBoardingManager() || this.userAuthorisationService.hasOnBoardingTeamLeader()) {
        this.isOnboardingAdminPortalRole = true;
      }

      if (this.userAuthorisationService.hasBrokerRole()) {
        this.isBrokerPortalRole = true;
      }

      this.showPoweredByOverride = environment.showPoweredByOverride;

      // Prevent API calls for PP users
      if (!this.isProviderPorterRole) {
        this.countOpenTickets();
        if (this.showCases) {
          this.countOpenCases();
        }
      }
    });
  }

  openSupport() {
    if (this.userAuthorisationService.hasRoleProvider()) {
      this.router.navigate(['/provider/support']);
    }
    else {
      this.router.navigate(['/support']);
    }
  }

  openAccount() {
    this.keycloakService.loadUserProfile().then((profile) => {
      const displayList: string[] = [];
      displayList.push('Name;Username;Email');
      displayList.push(`${profile.firstName} ${profile.lastName ?? ''};${profile.username ?? ''};${profile.email ?? ''}`);
      this.dialog.open(GenericListDialogComponent, {
        width: '40vw',
        data: {list: displayList, heading: 'Account Details'}
      });
    });
  }

  onClickOpenMemberLookup(): void {
    this.dialog.open(MemberLookupDialogComponent, {
      width: '40vw'
    });
  }

  signOut() {
    this.keycloakService.logout();
  }

  openCasesDisplayText(): string {
    let text = 'No open cases';
    if (this.numberOfOpenCases > 0) {
      text = `View ${this.numberOfOpenCases} open cases`;
    }
    return text;
  }

  openTicketsDisplayText(): string {
    let text = 'No open issues';
    if (this.numberOfOpenTickets > 0) {
      text = `View ${this.numberOfOpenTickets} open issues`;
    }
    return text;
  }

  openLinkInNewTab(caseString: string) {
    let url;
    switch (caseString) {
    case 'Rules Engine': {
      url = environment.baseEngineUrl + '/engine';
      break;
    }
    case 'Rules Author': {
      url = environment.baseAuthorUrl + '/author';
      break;
    }
    default: {
      url = 'https://dev.greydom.com';
      break;
    }
    }
    window.open(url, '_blank');
  }

  refreshEnterpriseCache() {
    this.cacheService.refreshEnterpriseCache().subscribe(
      () => {
        this.sharedService.openSnackBar('Enterprise cache refreshed successfully', 'Dismiss');
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  refreshClaimsCache() {
    this.cacheService.refreshClaimsCache().subscribe(
      () => {
        this.sharedService.openSnackBar('Claims cache refreshed successfully', 'Dismiss');
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  refreshWorkflowCache() {
    this.cacheService.refreshWorkflowCache().subscribe(
      () => {
        this.sharedService.openSnackBar('Workflow cache refreshed successfully', 'Dismiss');
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  onClickOpenIdVerification(): void {
    this.dialog.open(AdhocIdVerificationDialogComponent, {
      width: '40vw'
    });
  }

  onClickOpenBankVerification(): void {
    this.dialog.open(AdhocBankVerificationDialogComponent, {
      width: '40vw'
    });
  }

  private hasMemberVerificationAccess(): boolean {
    return (
      this.userAuthorisationService.hasRoleEnterpriseManager() ||
      this.userAuthorisationService.hasOnBoardingManager()
    );
  }

  private countOpenTickets() {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblIssueTicket;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 1;

    const statusPagedFilterDTO = new PagedFilterDTO();
    statusPagedFilterDTO.name = SortFilterValue.TICKET_STATUS;
    statusPagedFilterDTO.value = OPEN_STATUS;

    const assigneeFirstNamePagedFilterDTO = new PagedFilterDTO();
    assigneeFirstNamePagedFilterDTO.name = SortFilterValue.TICKET_ASSIGNEE_FIRST_NAME;
    assigneeFirstNamePagedFilterDTO.value = this.userFirstName;

    const assigneeLastNamePagedFilterDTO = new PagedFilterDTO();
    assigneeLastNamePagedFilterDTO.name = SortFilterValue.TICKET_ASSIGNEE_LAST_NAME;
    assigneeLastNamePagedFilterDTO.value = this.userLastName;

    pagedRequestDTO.fieldMap = {};
    pagedRequestDTO.fieldMap[SortFilterValue.TICKET_STATUS.split('.')[0]] = statusPagedFilterDTO;
    pagedRequestDTO.fieldMap[SortFilterValue.TICKET_ASSIGNEE_FIRST_NAME.split('.')[0] + '_1'] = assigneeFirstNamePagedFilterDTO;
    pagedRequestDTO.fieldMap[SortFilterValue.TICKET_ASSIGNEE_LAST_NAME.split('.')[0] + '_2'] = assigneeLastNamePagedFilterDTO;

    this.ticketService.getListOfIssueTickets(pagedRequestDTO).subscribe(
      (response) => {
        this.numberOfOpenTickets = response.totalRecords;
      }
    );
  }

  private countOpenCases() {
    this.caseService.getCaseCount(this.loggedInUserName, OPEN_STATUS).subscribe(
      (response) => {
        this.numberOfOpenCases = response;
      }
    );
  }
}
